// ---------- Vote Alerts: client-side proposal watch list + browser alerts ----------
// Lets a visitor "watch" an active governance action and get a browser
// notification before its voting window closes. The explorer has no login, so
// the watch list lives in localStorage (per-device) and the alert fires from a
// lightweight in-app runner while the site is open — no backend, no account.
// Each watched item stores the live `closeTs` computed on the proposal page, so
// the runner never guesses a deadline.
import { useEffect, useState } from "react";
import { LOCAL_API_URL } from "@/lib/constants";

const KEY = "ada-gov-watchlist-v1";
const EVT = "ada-gov-watch-changed";

export function getWatchlist() {
  try {
    return JSON.parse(localStorage.getItem(KEY)) || {};
  } catch {
    return {};
  }
}

function save(map) {
  localStorage.setItem(KEY, JSON.stringify(map));
  window.dispatchEvent(new Event(EVT));
}

export const watchKey = (tx, cert) => `${tx}:${cert}`;

export function isWatched(tx, cert) {
  return !!getWatchlist()[watchKey(tx, cert)];
}

export function addWatch(item) {
  const m = getWatchlist();
  const k = watchKey(item.txHash, item.certIndex);
  m[k] = { ...item, notified: m[k]?.notified || {}, addedAt: m[k]?.addedAt || Date.now() };
  save(m);
}

export function removeWatch(tx, cert) {
  const m = getWatchlist();
  delete m[watchKey(tx, cert)];
  save(m);
}

// Fills in closeTs for entries saved before the live deadline was known
// (e.g. watched from the digest before epoch data resolved).
export function backfillCloseTs(entries) {
  const m = getWatchlist();
  let changed = false;
  entries.forEach((e) => {
    if (e.closeTs == null) return;
    const k = watchKey(e.txHash, e.certIndex);
    if (m[k] && m[k].closeTs == null) {
      m[k].closeTs = e.closeTs;
      changed = true;
    }
  });
  if (changed) save(m);
}

function markNotified(k, ids) {
  const m = getWatchlist();
  if (!m[k]) return;
  const n = { ...(m[k].notified || {}) };
  ids.forEach((id) => { n[id] = true; });
  m[k].notified = n;
  save(m);
}

export async function ensureNotifyPermission() {
  if (!("Notification" in window)) return "unsupported";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  try {
    return await Notification.requestPermission();
  } catch {
    return "denied";
  }
}

export function humanLeft(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// Reactive subscription to one proposal's watched flag.
export function useWatched(txHash, certIndex) {
  const [watched, setWatched] = useState(() => isWatched(txHash, certIndex));
  useEffect(() => {
    const sync = () => setWatched(isWatched(txHash, certIndex));
    sync();
    window.addEventListener(EVT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(EVT, sync);
      window.removeEventListener("storage", sync);
    };
  }, [txHash, certIndex]);
  return watched;
}

// Reactive subscription to the whole watch list.
export function useWatchlist() {
  const [list, setList] = useState(getWatchlist);
  useEffect(() => {
    const sync = () => setList(getWatchlist());
    window.addEventListener(EVT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(EVT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  return list;
}

const H24 = 24 * 3600 * 1000;
const H1 = 1 * 3600 * 1000;

// Mounted once (App). Every minute it checks watched actions and fires at most
// one browser notification per item when it crosses the 24h / 1h marks (and one
// when voting has closed). Only runs with granted permission + the site open.
export function useVoteAlertRunner() {
  useEffect(() => {
    const base = (LOCAL_API_URL || "").replace(/\/+$/, "");

    const fire = (titleText, body, url, k, ids) => {
      try {
        const n = new Notification(titleText, { body, tag: `gov-${k}`, icon: "/favicon.ico" });
        n.onclick = () => { window.focus(); window.location.assign(url); };
        markNotified(k, ids);
      } catch {
        /* notification construction can throw on some browsers — ignore */
      }
    };

    const tick = () => {
      if (!("Notification" in window) || Notification.permission !== "granted") return;
      const m = getWatchlist();
      const now = Date.now();
      Object.entries(m).forEach(([k, it]) => {
        if (!it || !it.closeTs) return;
        const remaining = it.closeTs - now;
        const notified = it.notified || {};
        const label = it.title || "Governance action";
        const url = `${base}/governance/proposals/${it.txHash}/${it.certIndex}`;
        if (remaining > 0) {
          if (remaining <= H1 && !notified.h1) {
            fire("Vote closing soon", `${label} — voting closes in ${humanLeft(remaining)}.`, url, k, ["h1", "h24"]);
          } else if (remaining <= H24 && !notified.h24) {
            fire("Vote closing soon", `${label} — voting closes in ${humanLeft(remaining)}.`, url, k, ["h24"]);
          }
        } else if (!notified.closed) {
          fire("Voting has closed", `${label} — the voting window has ended.`, url, k, ["closed"]);
        }
      });
    };

    tick();
    const iv = setInterval(tick, 60000);
    return () => clearInterval(iv);
  }, []);
}
